/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a library entry owes, and the sentences that say so** — `X44`, `XS3` `E5` (`#1273`).
 *
 * Three surfaces answer *does this entry say what it is for*: the library editor, which asks for
 * the sentence; the library list, which marks the rows that have none; and the picker, which is
 * the reason the sentence is required at all. Written three times they would be three sentences
 * about one fact, drifting where nothing checks them — *shared logic is not shared words*
 * (`#1187`), and an explanation is read at exactly the moment an operator is most likely to
 * believe it.
 *
 * It lives outside the page for a second reason worth stating: a page that exports rules alongside
 * components loses fast refresh, and the rule was never the page's anyway.
 */

/** The blocking errors a library entry can carry. Keyed by field, so a form can render each one. */
export interface LibraryEntryBlockingErrors {
  name?: string;
}

/**
 * **What actually stops a save**, and the list is meant to stay this short.
 *
 * *Block on an invalid value, never on a missing one* leaves exactly two grounds: a value that
 * fails its own validator, and the structurally impossible — of which **identity** is one. A
 * library entry with no name cannot be referred to, so it is the second. Everything else about
 * this form, the required description included, is an **error**: it says so and lets the operator
 * proceed. See {@link describeMissingDescription}, which is the other half of this rule.
 */
export function blockingErrors(form: { name: string }): LibraryEntryBlockingErrors {
  const errors: LibraryEntryBlockingErrors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  return errors;
}

/**
 * **The description is required and it does not block the save.**
 *
 * Required-ness and blocking are two different claims, and this repo has ratified both: *block on
 * an invalid value, never on a missing one*, and `X46`'s split — a missing required value is an
 * **error**, which is *wrong, and you may proceed*. A library entry's **name** blocks because it
 * is identity, which is on that rule's short structural list; a description is not.
 *
 * So the error stands for as long as the box is empty, and an operator who saves anyway keeps the
 * expression they were building and gets a row the list marks. Refusing would make the console
 * stricter than the thing it authors — the library has always accepted an entry with no
 * description and still does.
 *
 * @returns the error to show, or `null` when the description is there.
 */
export function describeMissingDescription(description: string): string | null {
  return description.trim()
    ? null
    : 'Required. This sentence is what an operator reads when they reach for this expression, so an expression nobody can describe will not be picked.';
}

/**
 * **What a row with no description says, wherever a row is drawn.**
 *
 * The list marks it because a reader assumes a library entry says what it is for, so the entry
 * that does not is the exception (`#1248`); the picker says it because a blank second line reads
 * as a rendering fault rather than as a fact. One sentence, so the two cannot disagree.
 */
export const NO_DESCRIPTION_MARK = 'No description — this entry does not say what it is for.';

/**
 * **What a library search matches: everything the list renders.**
 *
 * `X44` asks an operator to find an expression by *what it does* rather than by its name, so the
 * row leads with the author's sentence and carries the expression's own read-out beneath it. A
 * filter narrower than that would let a row show `session.device.ip` while a search for
 * `device.ip` returned nothing — which reads as a broken search, not as a scoped one, and is the
 * shape this frame keeps paying for: **a reader narrower than the surface that writes**.
 *
 * The predicate lives here rather than in the page so it can be asserted directly; a filter tested
 * only by the markup around it is tested by proxy.
 */
export function matchesLibraryQuery(
  entry: { name: string; description?: string; tags?: string[] },
  readOut: string,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    entry.name.toLowerCase().includes(q) ||
    (entry.description ?? '').toLowerCase().includes(q) ||
    (entry.tags ?? []).some((tag) => tag.toLowerCase().includes(q)) ||
    readOut.toLowerCase().includes(q)
  );
}
